import { expand, flatten } from 'jsonld';

import { JsonLdResult, JsonLdResults } from 'bones/utils/jsonld/types';

const transform = async <T>(result: JsonLdResults) => {
  const expanded = await expand(result as any);
  const flattened = await flatten(expanded as any);
  return flattened as any as Array<T & JsonLdResult>;
};

export const formatJsonLd = async <T>(
  result: Array<T & JsonLdResult> | JsonLdResults
): Promise<Array<T & JsonLdResult>> => {
  if (Array.isArray(result)) {
    return result;
  }

  // set the vocabulary mapping as the base IRI as Stardog uses it to prefix IRIs,
  // but the jsonld library is written according to its non-normative definition
  // (see https://www.w3.org/TR/json-ld11/#default-vocabulary)
  const context = result['@context'];
  const vocabMapping = context?.['@vocab'];
  if (context && vocabMapping) {
    context['@base'] = vocabMapping;
  }

  // expand edge properties separately since they are not supported in the base json-ld spec
  const edgeProperties: Record<string, T & JsonLdResult> = {};
  await Promise.all(
    result['@graph'].map(async (value) => {
      const subject = value['@id'];
      if (subject && typeof subject !== 'string') {
        const key = `edge:${JSON.stringify(subject)}`;
        const [edge] = await transform<T>({
          ...result,
          '@graph': [subject],
        });

        edgeProperties[key] = edge;
        // eslint-disable-next-line no-param-reassign
        value['@id'] = key;
      }
    })
  );

  const expanded = await transform<T>(result);

  // restore edge properties
  expanded.forEach((value) => {
    const edgeProperty = edgeProperties[value['@id'] as string];
    if (edgeProperty) {
      // eslint-disable-next-line no-param-reassign
      value['@id'] = edgeProperty;
    }
  });

  return expanded;
};
